import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-[#050505]">
      <div className="max-w-screen-lg mx-auto px-6 md:px-12 py-16">
        <Link 
          to="/" 
          className="text-neutral-400 hover:text-white transition-colors text-sm mb-8 inline-block"
        >
          ← Back to Home
        </Link>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-3xl md:text-4xl font-bold text-white uppercase tracking-tight mb-8">
            Privacy Policy
          </h1>
          
          <p className="text-neutral-400 text-sm mb-8">
            Last updated: {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
          
          <div className="space-y-8 text-neutral-300 leading-relaxed">
            <section>
              <h2 className="text-xl font-semibold text-white mb-4">1. Introduction</h2>
              <p>
                Simply Smarter Ltd ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, and safeguard your information when you visit our website simplysmarter.io.
              </p>
              <p className="mt-4">
                Simply Smarter Ltd is a company registered in Scotland (Registration Number: SC867185) with its registered office at Clyde Offices, 2nd floor, 48 West George Street, Glasgow, U.K., G2 1BP.
              </p>
            </section>
            
            <section>
              <h2 className="text-xl font-semibold text-white mb-4">2. Information We Collect</h2>
              <p>We may collect the following types of information:</p>
              <ul className="list-disc list-inside mt-4 space-y-2 text-neutral-400">
                <li><strong className="text-neutral-300">Usage Data:</strong> Information about how you access and use our website, including your IP address, browser type, pages visited, and time spent on pages.</li>
                <li><strong className="text-neutral-300">Cookies:</strong> Small data files stored on your device. See our Cookie Policy section below for more details.</li>
                <li><strong className="text-neutral-300">Contact Information:</strong> If you contact us via email, we will collect your email address and any information you provide in your message.</li>
              </ul>
            </section>
            
            <section>
              <h2 className="text-xl font-semibold text-white mb-4">3. How We Use Your Information</h2>
              <p>We use the information we collect to:</p>
              <ul className="list-disc list-inside mt-4 space-y-2 text-neutral-400">
                <li>Operate and maintain our website</li>
                <li>Improve and personalise your experience</li>
                <li>Analyse usage patterns and trends</li>
                <li>Respond to your enquiries and communications</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>
            
            <section>
              <h2 className="text-xl font-semibold text-white mb-4">4. Legal Basis for Processing</h2>
              <p>Under UK GDPR, we process your personal data based on:</p>
              <ul className="list-disc list-inside mt-4 space-y-2 text-neutral-400">
                <li><strong className="text-neutral-300">Consent:</strong> Where you have given clear consent for us to process your personal data.</li>
                <li><strong className="text-neutral-300">Legitimate Interests:</strong> Where processing is necessary for our legitimate business interests.</li>
                <li><strong className="text-neutral-300">Legal Obligation:</strong> Where we need to comply with a legal requirement.</li>
              </ul>
            </section>
            
            <section>
              <h2 className="text-xl font-semibold text-white mb-4">5. Data Retention</h2>
              <p>
                We retain your personal data only for as long as necessary to fulfil the purposes for which it was collected, or as required by law. Analytics data is typically retained for 26 months.
              </p>
            </section>
            
            <section>
              <h2 className="text-xl font-semibold text-white mb-4">6. Your Rights</h2>
              <p>Under UK GDPR, you have the right to:</p>
              <ul className="list-disc list-inside mt-4 space-y-2 text-neutral-400">
                <li>Access your personal data</li>
                <li>Rectify inaccurate personal data</li>
                <li>Request erasure of your personal data</li>
                <li>Restrict processing of your personal data</li>
                <li>Data portability</li>
                <li>Object to processing</li>
                <li>Withdraw consent at any time</li>
              </ul>
              <p className="mt-4">
                To exercise any of these rights, please contact us at hello@simplysmarter.io.
              </p>
            </section>
            
            <section>
              <h2 className="text-xl font-semibold text-white mb-4">7. Third-Party Services</h2>
              <p>
                Our website may contain links to third-party websites. We are not responsible for the privacy practices of these external sites. We encourage you to read the privacy policies of any third-party sites you visit.
              </p>
            </section>
            
            <section>
              <h2 className="text-xl font-semibold text-white mb-4">8. Data Security</h2>
              <p>
                We implement appropriate technical and organisational measures to protect your personal data against unauthorised access, alteration, disclosure, or destruction.
              </p>
            </section>
            
            <section>
              <h2 className="text-xl font-semibold text-white mb-4">9. Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated revision date.
              </p>
            </section>
            
            <section>
              <h2 className="text-xl font-semibold text-white mb-4">10. Contact Us</h2>
              <p>
                If you have any questions about this Privacy Policy, please contact us at:
              </p>
              <address className="mt-4 not-italic text-neutral-400">
                Simply Smarter Ltd<br />
                Clyde Offices, 2nd floor<br />
                48 West George Street<br />
                Glasgow, U.K.<br />
                G2 1BP<br />
                Email: <a href="mailto:hello@simplysmarter.io" className="text-white hover:underline">hello@simplysmarter.io</a>
              </address>
            </section>
            
            <section>
              <h2 className="text-xl font-semibold text-white mb-4">11. Supervisory Authority</h2>
              <p>
                You have the right to lodge a complaint with the Information Commissioner's Office (ICO) if you believe your data protection rights have been violated. Visit <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-white hover:underline">ico.org.uk</a> for more information.
              </p>
            </section>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
